import React, { useEffect, useRef, useState } from 'react';
import { FaRocket } from 'react-icons/fa';
import { FiSettings, FiZap, FiTrendingUp, FiCheckCircle, FiCalendar, FiArrowRight, FiTarget, FiRocket } from 'react-icons/fi';

const RoadmapSection = () => {
  const [isVisible, setIsVisible] = useState({
    header: false,
    phases: []
  });

  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const phasesRef = useRef([]);

  const phases = [
    {
      id: '01',
      phase: 'PHASE 01',
      duration: 'WEEKS 1 - 2',
      title: 'Planning & Architecture',
      icon: <FiSettings className="w-5 h-5" />,
      description: "We understand your product vision, define system architecture, and choose the right tech stack to ensure scalability, performance, and long-term maintainability.",
      points: [
        'Requirement Analysis',
        'System Architecture Design',
        'Tech Stack Selection',
        'Database & API Planning'
      ],
      align: 'left',
      gradient: 'from-emerald-500 to-cyan-500',
      iconColor: '#10b981',
      stat: 'Foundation Ready',
      statIcon: <FiTarget className="w-3 h-3" />
    },
    {
      id: '02',
      phase: 'PHASE 02',
      duration: 'WEEKS 3 - 4',
      title: 'Development & Implementation',
      icon: <FiZap className="w-5 h-5" />,
      description: "We build your software using clean, scalable code. Core features, APIs, and user interfaces are developed with continuous testing to ensure stability and performance.",
      points: [
        'Frontend & Backend Development',
        'API Development & Integration',
        'Core Feature Implementation',
        'Testing & QA'
      ],
      align: 'right',
      gradient: 'from-violet-500 to-purple-500',
      iconColor: '#a855f7',
      stat: 'MVP in Progress',
      statIcon: <FaRocket className="w-3 h-3" />
    },
    {
      id: '03',
      phase: 'PHASE 03',
      duration: 'WEEKS 5+, ONGOING',
      title: 'Launch, Optimization & Scaling',
      icon: <FiTrendingUp className="w-5 h-5" />,
      description: "We deploy your application, optimize performance, and continuously improve scalability, security, and user experience as your product grows.",
      points: [
        'Deployment & Go-Live',
        'Performance Optimization',
        'Security Enhancements',
        'Scaling & Continuous Improvement'
      ],
      align: 'left',
      gradient: 'from-amber-500 to-orange-500',
      iconColor: '#f59e0b',
      stat: 'Production Ready',
      statIcon: <FiTrendingUp className="w-3 h-3" />
    }
  ];
  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -50px 0px'
    };

    const headerObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(prev => ({ ...prev, header: true }));
          headerObserver.disconnect();
        }
      });
    }, observerOptions);

    const phaseObservers = phasesRef.current.map((phase, index) => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              phases: [...prev.phases, index]
            }));
            observer.disconnect();
          }
        });
      }, { ...observerOptions, threshold: 0.3 });

      if (phase) observer.observe(phase);
      return observer;
    });

    if (headerRef.current) headerObserver.observe(headerRef.current);

    return () => {
      headerObserver.disconnect();
      phaseObservers.forEach(observer => observer.disconnect());
    };
  }, []);

  return (
    <section ref={sectionRef} className="bg-black text-white py-28 px-6 font-sans relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-40 left-20 w-96 h-96 bg-emerald-500/5 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-cyan-500/5 rounded-full blur-[100px] animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-[130px]"></div>

        {/* Diagonal Lines Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_65%,rgba(14,214,183,0.02)_75%,transparent_85%)] bg-[size:30px_30px]"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header Section with Animation */}
        <div
          ref={headerRef}
          className={`text-center mb-24 transition-all duration-700 delay-200 transform ${isVisible.header ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`}
        >
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 backdrop-blur-sm px-4 py-2 rounded-full border border-emerald-500/30 mb-6">
            <FiCalendar className="text-emerald-400 w-4 h-4 animate-pulse" />
            <span className="text-emerald-400 text-xs font-bold tracking-[2px] uppercase">YOUR ROADMAP</span>
          </div>

          <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6 leading-tight">
            From Strategy to
            <span className="relative inline-block mx-2">
              <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                Qualified Pipeline
              </span>
              <svg className="absolute -bottom-2 left-0 w-full h-3" viewBox="0 0 200 8" fill="none">
                <path d="M0 4 L200 4" stroke="url(#gradientLineRoad)" strokeWidth="2" strokeDasharray="5 5" />
                <defs>
                  <linearGradient id="gradientLineRoad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#10b981" />
                    <stop offset="100%" stopColor="#06b6d4" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
            <br />
            <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
              in Three Phases
            </span>
          </h2>

          <p className="max-w-2xl mx-auto text-gray-400 text-lg leading-relaxed">
            A systematic approach with clear milestones. You'll know exactly what's happening, when, and why.
          </p>
        </div>

        {/* Roadmap Timeline Container */}
        <div className="max-w-6xl mx-auto relative">

          {/* Animated Central Vertical Line */}
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-[2px] h-full">
            <div className="absolute inset-0 bg-gradient-to-b from-emerald-500 via-cyan-500 to-purple-500 animate-pulse"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-500/50 to-transparent animate-ping opacity-20"></div>
          </div>

          <div className="space-y-20 md:space-y-0">
            {phases.map((item, index) => (
              <div
                key={index}
                ref={el => phasesRef.current[index] = el}
                className={`relative flex flex-col md:flex-row items-center justify-between transition-all duration-700 transform ${isVisible.phases.includes(index)
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-16 opacity-0'
                  }`}
                style={{ transitionDelay: `${300 + index * 150}ms` }}
              >

                {/* Content Card */}
                <div className={`w-full md:w-[45%] ${item.align === 'right' ? 'md:order-last' : ''}`}>
                  <div className="group relative">
                    {/* Animated Glow Effect */}
                    <div className={`absolute -inset-0.5 bg-gradient-to-r ${item.gradient} rounded-2xl opacity-0 group-hover:opacity-30 blur transition-all duration-500`}></div>

                    <div className="relative bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 rounded-2xl p-8 hover:border-gray-700 transition-all duration-500 hover:shadow-2xl hover:-translate-y-1">

                      {/* Decorative Corner */}
                      <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${item.gradient} opacity-10 rounded-tr-2xl`}></div>

                      <div className="flex items-start gap-4 mb-5">
                        {/* Icon Container - FIXED: Simple container with border and no overlay */}
                        <div className="bg-gray-800/50 p-3 rounded-xl border border-gray-700 group-hover:border-gray-600 transition-all duration-300 group-hover:scale-110">
                          {/* Icon rendered directly with inline color */}
                          <div style={{ color: item.iconColor }}>
                            {item.icon}
                          </div>
                        </div>

                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-3 mb-2">
                            <span className={`text-transparent bg-gradient-to-r ${item.gradient} bg-clip-text text-[10px] font-bold tracking-widest`}>
                              {item.phase}
                            </span>
                            <span className="text-gray-500 text-[10px] font-mono">•</span>
                            <span className="text-gray-500 text-[10px] font-mono tracking-wide">
                              {item.duration}
                            </span>
                          </div>
                          <h3 className="text-2xl font-bold tracking-tight group-hover:text-white transition-colors">
                            {item.title}
                          </h3>
                        </div>
                      </div>

                      <p className="text-gray-400 text-sm leading-relaxed mb-6 group-hover:text-gray-300 transition-colors">
                        {item.description}
                      </p>

                      {/* Points Grid with Check Icons */}
                      <div className="grid grid-cols-2 gap-3 mb-6">
                        {item.points.map((point, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs text-gray-300 group-hover:text-gray-200 transition-colors">
                            <FiCheckCircle style={{ color: item.iconColor }} className="w-3 h-3 flex-shrink-0" />
                            <span>{point}</span>
                          </div>
                        ))}
                      </div>

                      {/* Status Badge */}
                      <div className={`flex items-center justify-between pt-4 border-t border-gray-800`}>
                        <div className="flex items-center gap-2">
                          <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${item.gradient} animate-pulse`}></div>
                          <span className="text-[10px] text-gray-500 tracking-wide">STATUS</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div style={{ color: item.iconColor }}>
                            {item.statIcon}
                          </div>
                          <span className={`text-xs font-medium bg-gradient-to-r ${item.gradient} bg-clip-text text-transparent`}>
                            {item.stat}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Center Timeline Node */}
                <div className="absolute left-1/2 transform -translate-x-1/2 flex flex-col items-center z-20 hidden md:flex">
                  {/* Animated Pulse Ring */}
                  <div className="relative">
                    <div className={`absolute inset-0 rounded-full bg-gradient-to-r ${item.gradient} blur-md animate-ping opacity-40`} style={{ width: '48px', height: '48px' }}></div>
                    <div className={`relative w-12 h-12 rounded-full bg-gradient-to-br ${item.gradient} flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110`}>
                      <span className="text-black font-black text-sm">{item.id}</span>
                    </div>
                  </div>

                  {/* Duration Label */}
                  <div className="mt-3 px-2 py-1 bg-black/80 backdrop-blur-sm rounded-full border border-gray-800">
                    <span className={`text-[9px] font-bold bg-gradient-to-r ${item.gradient} bg-clip-text text-transparent whitespace-nowrap`}>
                      {item.duration.split(',')[0]}
                    </span>
                  </div>

                  {/* Connecting Line to Card */}
                  <div className={`absolute top-1/2 ${item.align === 'left' ? '-right-8' : '-left-8'} w-8 h-[2px] bg-gradient-to-r ${item.gradient} opacity-50 hidden md:block`}></div>
                </div>

                {/* Empty Space for Desktop Layout */}
                <div className="hidden md:block w-[45%]"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA Section */}
        <div className="mt-24 text-center">
          <div className="relative group max-w-3xl mx-auto">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-500 via-cyan-500 to-purple-500 rounded-2xl opacity-0 group-hover:opacity-50 blur transition duration-500"></div>

            <div className="relative bg-gradient-to-br from-gray-900/80 to-gray-950/80 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 group-hover:border-emerald-500/30 transition-all duration-500">

              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Ready to turn your idea into a scalable, high-performance software product built for real-world growth?
              </p>

              <button className="group/btn relative inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full font-semibold text-black hover:shadow-lg hover:shadow-emerald-500/25 transition-all duration-300 hover:scale-105">
                <span>Start Building Your Product</span>
                <FiArrowRight className="group-hover/btn:translate-x-1 transition-transform duration-300" />
              </button>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoadmapSection;